import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getOwnerStats } from "store/actions/admin/clients";
import { trackEvent } from "utils/analytics";

const StatCard = ({ title, children }) => (
  <Card variant="outlined" sx={{ borderRadius: "12px", height: "100%" }}>
    <CardContent>
      <Typography
        fontSize="11px"
        fontWeight={700}
        color="#757E97"
        textTransform="uppercase"
        letterSpacing="0.5px"
        mb={1.5}
      >
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const StatRow = ({ label, value, color }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
    <Typography fontSize="13px" color="#4F5668">
      {label}
    </Typography>
    <Typography fontSize="13px" fontWeight={600} color={color || "#111111"}>
      {value ?? "-"}
    </Typography>
  </Box>
);

const BigStat = ({ value, sub }) => (
  <Box>
    <Typography fontSize="28px" fontWeight={700} color="#111111" lineHeight={1}>
      {value ?? "-"}
    </Typography>
    {sub && (
      <Typography fontSize="12px" color="#757E97" mt={0.5}>
        {sub}
      </Typography>
    )}
  </Box>
);

const subscriptionColor = (type) => {
  if (!type) return "default";
  const t = type.toUpperCase();
  if (t === "FREE") return "default";
  if (t.includes("PREMIUM") || t.includes("ENTERPRISE")) return "primary";
  return "secondary";
};

const OwnerDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();

  const { selectedOwnerStats: data, isLoadingOwnerStats: loading } =
    useSelector((state) => state.admin.clients);

  useEffect(() => {
    if (orgId) {
      dispatch(getOwnerStats(orgId));
      trackEvent("Admin", "owner_detail_viewed");
    }
  }, [orgId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/clients")}
          sx={{ mb: 2, textTransform: "none" }}
        >
          Retour à la liste
        </Button>
        <Typography color="error">Données introuvables.</Typography>
      </Box>
    );
  }

  const { owner, organization, stats } = data;
  const subType = organization?.subscription?.type;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100, margin: "auto" }}>
      {/* Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/admin/clients")}
        sx={{ mb: 3, textTransform: "none", color: "#4F5668" }}
      >
        Retour à la liste
      </Button>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={0.5}>
        <Typography fontSize="22px" fontWeight={700}>
          {owner?.first_name || owner?.last_name
            ? `${owner?.first_name || ""} ${owner?.last_name || ""}`.trim()
            : "—"}
        </Typography>
        {owner?.status && (
          <Chip
            label={owner.status}
            size="small"
            color={owner.status === "Actif" ? "success" : "default"}
            sx={{ fontWeight: 600 }}
          />
        )}
        {subType && (
          <Chip
            label={subType}
            size="small"
            color={subscriptionColor(subType)}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>
      <Typography fontSize="13px" color="#757E97" mb={3}>
        {owner?.email}
        {stats?.account_age_days != null && ` · Membre depuis ${stats.account_age_days} jours`}
        {stats?.days_since_last_login != null
          ? ` · Dernière connexion il y a ${stats.days_since_last_login} jour${stats.days_since_last_login !== 1 ? "s" : ""}`
          : " · Dernière connexion : Jamais"}
      </Typography>

      {/* Profile + Subscription */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <StatCard title="Profil">
            <StatRow label="Email" value={owner?.email} />
            <StatRow label="Membre depuis" value={owner?.created_on ? new Date(owner.created_on).toLocaleDateString("fr-FR") : "-"} />
            <StatRow
              label="Dernière connexion"
              value={
                owner?.last_login
                  ? new Date(owner.last_login).toLocaleDateString("fr-FR")
                  : "Jamais"
              }
              color={!owner?.last_login ? "#E53E3E" : stats?.days_since_last_login > 30 ? "#E53E3E" : stats?.days_since_last_login > 7 ? "#D97706" : "#38A169"}
            />
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard title="Abonnement">
            <StatRow label="Plan" value={organization?.subscription?.name || subType || "-"} />
            <StatRow
              label="Début"
              value={organization?.subscription?.start_date ? new Date(organization.subscription.start_date).toLocaleDateString("fr-FR") : "-"}
            />
            <StatRow
              label="Fin"
              value={organization?.subscription?.end_date ? new Date(organization.subscription.end_date).toLocaleDateString("fr-FR") : "-"}
            />
            {stats?.days_until_expiry != null && (
              <StatRow
                label="Jours restants"
                value={stats.days_until_expiry}
                color={stats.days_until_expiry < 30 ? "#E53E3E" : stats.days_until_expiry < 60 ? "#D97706" : "#38A169"}
              />
            )}
          </StatCard>
        </Grid>
      </Grid>

      {/* Seat stats */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <StatCard title="Coachs">
            <BigStat
              value={stats?.coach_seat_utilization || stats?.coaches_count}
              sub={stats?.coach_seat_utilization ? "places utilisées" : "coach(s)"}
            />
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Clients">
            <BigStat
              value={stats?.client_seat_utilization || stats?.clients_count}
              sub={stats?.client_seat_utilization ? "places utilisées" : "client(s)"}
            />
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Activité clients">
            <StatRow label="Actifs" value={stats?.active_clients_count} />
            <StatRow label="En attente" value={stats?.pending_clients_count} />
            <StatRow label="Workout 30j" value={stats?.clients_active_last_30_days} />
          </StatCard>
        </Grid>
      </Grid>

      {/* Content stats */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Programmes">
            <BigStat value={stats?.programs_count} sub="créés" />
            {stats?.active_programs_count != null && (
              <Typography fontSize="12px" color="#757E97" mt={1}>
                {stats.active_programs_count} actif{stats.active_programs_count !== 1 ? "s" : ""}
              </Typography>
            )}
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Séances">
            <BigStat value={stats?.sessions_count} sub="créées" />
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Nutrition">
            <BigStat value={stats?.nutrition_programs_count} sub="créés" />
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Évolutions">
            <BigStat value={stats?.evolutions_count} sub="entrées" />
            {stats?.clients_with_evolutions != null && (
              <Typography fontSize="12px" color="#757E97" mt={1}>
                {stats.clients_with_evolutions} client{stats.clients_with_evolutions !== 1 ? "s" : ""}
              </Typography>
            )}
          </StatCard>
        </Grid>
      </Grid>

      {/* Session completions */}
      {stats?.total_session_completions != null && (
        <Box mt={2} p={2} sx={{ background: "#F5F5F5", borderRadius: "12px" }}>
          <Typography fontSize="13px" color="#4F5668">
            <strong>{stats.total_session_completions}</strong> séance
            {stats.total_session_completions !== 1 ? "s" : ""} complétée
            {stats.total_session_completions !== 1 ? "s" : ""} par les clients
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OwnerDetail;
